import "./SongsheetCarousel.css";
import { PlaylistItemData } from "../playlist/data/PlaylistItemData";
import PlaylistData from "../playlist/data/PlaylistData";
import { Button, ListGroup } from "reactstrap";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCirclePlus,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { PlaylistItemSong } from "../playlist/item/PlaylistItemSong";
import PlaylistItemInvalid from "../playlist/item/PlaylistItemInvalid";
import { SortablePlaylistItemSong } from "../playlist/edit/SortablePlaylistItemSong";
import { TitleEditable } from "../playlist/edit/TitleEditable";
import { UnkownEntriesWarning } from "../playlist/edit/UnkownEntriesWarning";
import { ImportedPlaylistInfo } from "../playlist/edit/ImportedPlaylistInfo";
import { PushNeededInfo } from "../playlist/edit/PushNeededInfo";
import { PlaylistManipulationUtils } from "../playlist/PlaylistManipulationUtils";
import { ItemId } from "../playlist/data/PlaylistTypes";
import { CyPlaylist } from "../playlist/CyPlaylist";
import { CyPlaylistFooterMenu } from "../playlist/CyPlaylistFooterMenu";
import ArrayUtils from "../common/ArrayUtils";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { isSidebarBesideSlides } from "./CarouselSidebar";
import Sidebar, { sidebarTitleClassName } from "./Sidebar";
import { scrollIntoViewInSidebar } from "./sidebar/scrollIntoViewInSidebar";
import { AddSongsSidebar } from "./sidebar/AddSongsSidebar";
import PlaylistBanner from "./PlaylistBanner";

interface Props {
  playlistData: PlaylistData;
  setTitle: (title: string) => void;
  setItems: (
    value:
      | ((prevState: PlaylistItemData[]) => PlaylistItemData[])
      | PlaylistItemData[],
  ) => void;
  selectedItem: PlaylistItemData | undefined;
  setSelectedItem: (item: PlaylistItemData | undefined) => void;
  closeSidebar: () => void;
  lookupSong: LookupSongFunction;
  bucket: string | undefined;
}

export default function CarouselPlaylistSidebar({
  playlistData,
  setTitle,
  setItems,
  selectedItem,
  setSelectedItem,
  closeSidebar,
  lookupSong,
  bucket,
}: Props) {
  const { t } = useTranslation();
  const [isAddingSongs, setIsAddingSongs] = useState(false);

  const items = playlistData.getItems();
  const isPlaylistEditable = !playlistData.isQrCodeScannedPlaylist;

  //keeps the selected item visible, whether it was selected here, in the banner or by paging in the carousel
  //on a wrapper, because the reactstrap ListGroup forwards neither ref nor innerRef
  const listRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledOnceRef = useRef(false);
  const selectedItemId = selectedItem?.id;
  useEffect(() => {
    const list = listRef.current;
    if (isAddingSongs || selectedItemId === undefined || !list) return;
    function scrollSelectedRowIntoView(behavior: ScrollBehavior) {
      const row = list?.querySelector<HTMLElement>(
        `[data-sortable-id="${selectedItemId}"]`,
      );
      if (row) scrollIntoViewInSidebar(row, behavior);
      return row !== null;
    }

    //jumping into place when the list appears, smooth afterwards to keep the orientation
    if (
      !scrollSelectedRowIntoView(hasScrolledOnceRef.current ? "smooth" : "auto")
    )
      return;
    hasScrolledOnceRef.current = true;

    //rows grow once their lyrics preview is loaded, which would push the selected row out of view again
    let isInitialObservation = true;
    const resizeObserver = new ResizeObserver(() => {
      //the observer reports the current size right away, which would cut the smooth scroll short
      if (isInitialObservation) isInitialObservation = false;
      else scrollSelectedRowIntoView("auto");
    });
    resizeObserver.observe(list);
    return () => resizeObserver.disconnect();
  }, [selectedItemId, isAddingSongs, items.length]);

  function itemClickedHandler(item: PlaylistItemData) {
    //the first click selects the item (e.g. to delete it), a second one shows it:
    //below xl the sidebar hides the slides, so close it
    if (item.id === selectedItem?.id && !isSidebarBesideSlides())
      closeSidebar();
    else setSelectedItem(item);
  }

  function renderRow(entry: PlaylistItemData): ReactNode {
    const item = lookupSong(entry.songEntryId) ? (
      <PlaylistItemSong lookupSong={lookupSong} playlistEntry={entry} />
    ) : (
      <PlaylistItemInvalid playlistEntry={entry} detailed />
    );

    return (
      <SortablePlaylistItemSong
        key={entry.id}
        id={entry.id}
        isSelected={selectedItem?.id === entry.id}
        onClick={() => itemClickedHandler(entry)}
        disabled={!isPlaylistEditable}
      >
        {item}
      </SortablePlaylistItemSong>
    );
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function onDragStart(event: DragStartEvent) {
    setSelectedItem(items.find((item) => item.id === event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over }: DragEndEvent = event;

    if (active && over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((e) => e.id === active.id);
        const newIndex = items.findIndex((e) => e.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  function removeInvalidItems() {
    setItems(items.filter((p) => lookupSong(p.songEntryId)));
  }

  //inserts after the selected item, so several songs picked in a row keep their order
  function insert(songEntryId: string) {
    const newItem = PlaylistManipulationUtils.createItemToInsert(
      items,
      songEntryId,
    );
    setItems(
      PlaylistManipulationUtils.insertAfter(
        playlistData,
        newItem,
        selectedItem?.id,
      ).getItems(),
    );
    setSelectedItem(newItem);
  }

  function remove(itemId: ItemId) {
    const nextItem = ArrayUtils.followingOf(items, (i) => i.id === itemId);
    const prevItem = ArrayUtils.previousOf(items, (i) => i.id === itemId);
    setItems(items.filter((i) => i.id !== itemId));
    setSelectedItem(nextItem || prevItem);
  }

  const title = (
    <TitleEditable
      content={playlistData.title}
      updateContent={setTitle}
      editAllowed={isPlaylistEditable}
      headingClassName={sidebarTitleClassName}
    />
  );

  const actions = isPlaylistEditable && (
    <div className="d-flex gap-2">
      {isAddingSongs ? (
        <Button
          color="primary"
          data-cy={CyPlaylist.ButtonDoneAdding}
          onClick={() => setIsAddingSongs(false)}
        >
          <FontAwesomeIcon icon={faCheck} /> {t("common.buttons.done")}
        </Button>
      ) : (
        <Button
          color="primary"
          data-cy={CyPlaylistFooterMenu.ButtonAdd}
          onClick={() => setIsAddingSongs(true)}
        >
          <FontAwesomeIcon icon={faCirclePlus} /> {t("common.buttons.add")}
        </Button>
      )}
      <Button
        color="primary"
        data-cy={CyPlaylist.ButtonRemove}
        disabled={selectedItem === undefined}
        onClick={() => {
          if (selectedItem) remove(selectedItem.id);
        }}
      >
        <FontAwesomeIcon icon={faTrashAlt} /> {t("common.buttons.delete")}
      </Button>
    </div>
  );

  if (isAddingSongs)
    return (
      <AddSongsSidebar
        closeSidebar={closeSidebar}
        title={title}
        actions={actions}
        banner={
          //the carousel shows the playlist banner next to the sidebar from xl on
          <div className="d-xl-none">
            <PlaylistBanner
              playlistData={playlistData}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              lookupSong={lookupSong}
            />
          </div>
        }
        songPicked={insert}
        bucket={bucket}
      />
    );

  const showUnknownEntriesWarning =
    isPlaylistEditable &&
    PlaylistManipulationUtils.hasInvalidItemsWithNoCEntry(items, lookupSong);
  const showPushNeededInfo = isPlaylistEditable && playlistData.needsPush;
  const showInfos =
    showUnknownEntriesWarning ||
    showPushNeededInfo ||
    playlistData.isQrCodeScannedPlaylist;

  return (
    <Sidebar closeSidebar={closeSidebar} title={title} actions={actions}>
      {showInfos && (
        <div className="d-flex flex-column gap-2 p-2">
          {showUnknownEntriesWarning && (
            <UnkownEntriesWarning removeInvalidItems={removeInvalidItems} />
          )}
          {playlistData.isQrCodeScannedPlaylist && (
            <ImportedPlaylistInfo playlistData={playlistData} />
          )}
          {showPushNeededInfo && <PushNeededInfo />}
        </div>
      )}

      <div ref={listRef}>
        <ListGroup flush>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={onDragStart}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) => renderRow(item))}
            </SortableContext>

            <DragOverlay>
              {selectedItem ? renderRow(selectedItem) : null}
            </DragOverlay>
          </DndContext>
        </ListGroup>
      </div>
    </Sidebar>
  );
}
